import { CustomToolKitParam, ToolConfigCustomToolKit, isToolConfigEnumParam } from "@shared/index";
import { RouteShareService } from "src/app/shared/services/route-share.service";
import { BlocklyHelperService } from "../blockly-helper.service";
import { BLOCKLY_COLORS, BlocklyToolboxCategory } from "../blocky-types.types";

/**
 * Nice
 * Name der dynamischen Kategorie – wird von BlocklyEditorComponent benutzt,
 * um eine zuvor eingefügte Instanz beim Editor-Wechsel wieder zu entfernen.
 */
export const WENNDANN_SPECIFIC_CATEGORY_NAME = "Wenn-Dann Spezifische Werte";

/**
 * CallbackKey für den Blockly-Flyout-Button, der direkt zum aktuell aktiven Wenn-Dann
 * (Detail-Drawer im "Wenn-Dann"-Tab) navigiert (registriert via workspace.registerButtonCallback).
 */
export const IFTHEN_CONFIGURE_VARS_CALLBACK_KEY = "IFTHEN_CONFIGURE_VARS";

// Stabile Block-Typen: werden bei jedem Editor-Wechsel mit frischen Dropdown-Optionen
// neu definiert (siehe BlocklyEditorComponent.refreshWennDannSpecificCategory()).
const IFTHEN_SPECIFIC_STRING_BLOCK = "IFTHEN_SPECIFIC_STRING";
const IFTHEN_SPECIFIC_NUMBER_BLOCK = "IFTHEN_SPECIFIC_NUMBER";
const IFTHEN_SPECIFIC_BOOLEAN_BLOCK = "IFTHEN_SPECIFIC_BOOLEAN";

function buildTypedVarDropdown(
  BH: BlocklyHelperService,
  uniqueBlockName: string,
  outputType: "String" | "Number" | "Boolean",
  prefix: string,
  options: [string, string][]
): { kind: 'block'; type: string } {
  const dropdownOptions = options.map(([text, value]) => (text + "|" + value));
  return BH._custom(
    uniqueBlockName,
    "@output:" + outputType + "\n" + prefix + "{{value:dropdown=" + dropdownOptions.join(",") + "}}",
    "{{value}}",
    { colour: BLOCKLY_COLORS.PROPERTIES, inputsInline: true }
  );
}

/**
 * Kontextabhängige Kategorie: zeigt Blockly-Blöcke für die Variablen (params) des
 * AKTUELL im Scripting-Editor ausgewählten Wenn-Dann. Gibt `null` zurück, wenn der
 * aktive Editor kein Wenn-Dann ist – in dem Fall soll die Kategorie in der Sidebar
 * komplett fehlen.
 */
export function BlocklyCategory_WENNDANN_SPECIFIC(
  name: string = WENNDANN_SPECIFIC_CATEGORY_NAME,
  BH: BlocklyHelperService,
  rss: RouteShareService
): BlocklyToolboxCategory | null {
  const activeIfthen = rss.editorItem
    ? rss.editorItems.ifthen.find(ifthen => ifthen.uniqueId === rss.editorItem?.uniqueId)
    : undefined;

  if (!activeIfthen) {
    return null;
  }

  // Bewusst OHNE enabled-Filter: das gerade bearbeitete Wenn-Dann soll seine eigenen
  // Variablen auch dann zeigen, wenn es selbst deaktiviert ist.
  const toolConfig = rss.botConfig.toolConfigs.find(tc => tc.id === activeIfthen.id) as ToolConfigCustomToolKit | undefined;
  const params: CustomToolKitParam[] = toolConfig?.params ?? [];
  const wennDannLabel = toolConfig?.skillId || toolConfig?.name || activeIfthen.name;

  const cat: BlocklyToolboxCategory = { kind: 'category', colour: BLOCKLY_COLORS.WENNDANN, name: name, contents: [] };
  cat.contents = [
    BH.label("Start & Ablauf > " + WENNDANN_SPECIFIC_CATEGORY_NAME),
    BH.label("Variablen für: " + wennDannLabel),
    // Crosslink: führt direkt zum bearbeiteten Wenn-Dann (Detail-Drawer), unabhängig
    // davon, ob bereits Variablen konfiguriert sind – steht daher immer direkt neben
    // den Info-Labels bzw. den Variablen-Dropdowns weiter unten.
    { kind: 'button', text: '🔗 Zum Wenn-Dann: ' + wennDannLabel, callbackKey: IFTHEN_CONFIGURE_VARS_CALLBACK_KEY } as any
  ];

  if (params.length === 0) {
    cat.contents.push(
      BH.label("Für dieses Wenn-Dann sind noch keine Variablen konfiguriert.")
    );
    return cat;
  }

  const stringParams = params.filter(p => p.type === "string" || isToolConfigEnumParam(p));
  const numberParams = params.filter(p => p.type === "number");
  const booleanParams = params.filter(p => p.type === "boolean");

  if (stringParams.length > 0) {
    cat.contents.push(buildTypedVarDropdown(BH, IFTHEN_SPECIFIC_STRING_BLOCK, "String", "Text-Variable: ", stringParams.map(p => [p.key, 'vars.' + p.key])));
  }
  if (numberParams.length > 0) {
    cat.contents.push(buildTypedVarDropdown(BH, IFTHEN_SPECIFIC_NUMBER_BLOCK, "Number", "Zahl-Variable: ", numberParams.map(p => [p.key, 'vars.' + p.key])));
  }
  if (booleanParams.length > 0) {
    cat.contents.push(buildTypedVarDropdown(BH, IFTHEN_SPECIFIC_BOOLEAN_BLOCK, "Boolean", "Boolean-Variable: ", booleanParams.map(p => [p.key, 'vars.' + p.key])));
  }

  return cat;
}
